import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
  LightDomStyles,
  OutlineElement,
  SlotsController,
} from '@phase2/outline-core';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './outline-video-modal.css.lit';
import buttonStyles from '../outline-button-baseline/outline-button-baseline.css.lit';
import '../outline-icon-baseline/outline-icon-baseline';

export interface OutlineVideoModalInterface extends HTMLElement {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * The Video Modal component
 * @element outline-video-modal
 *
 * @slot video-image- The trigger for the video modal
 * @slot video-summary - The content underneath the video
 * @slot accessibility-description - The accessibility description which is used by screen readers.
 *
 * @attr video-id
 * @attr full-image
 * @attr video-provider
 * @attr video-after
 */

@customElement('outline-video-modal')
export class OutlineVideoModal
  extends OutlineElement
  implements OutlineVideoModalInterface
{
  slots = new SlotsController(this);

  static styles: CSSResultGroup = [componentStyles, buttonStyles];

  lightDomStyles = new LightDomStyles(
    this,
    [componentStyles],

    this.tagName.toLowerCase()
  );

  @property({ attribute: false })
  isOpen = false;

  @query('iframe')
  private iFrame!: HTMLElement;

  _pauseVideo = () => {
    this.shadowRoot
      ?.querySelector('iframe')
      ?.contentWindow?.postMessage(
        '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
        '*'
      );
  };

  _closeAll() {
    if (this.iFrame) {
      this._pauseVideo();
      this.close();
    } else {
      this.close();
    }
  }

  render(): TemplateResult {
    return html`
      <section class="video-modal ${this.isOpen ? 'is-open' : null}">
        <button
          class="trigger"
          @click="${this.open}"
          aria-label="Play Video"
          focusable="true"
          @keydown="${this._handleTriggerKeydown}"
        >
          <div class="video-wrapper">
            ${this.playIconTemplate()}
            ${this.slots.conditionalSlot('video-image', false)}
          </div>
        </button>
        ${this.slots.conditionalSlot('video-summary', false)}
      </section>
      ${this._modalTemplate()}
    `;
  }

  updated() {
    // If a page resize occurs while a video is playing, restore the scrolling of the page (the component has been rerendered and the video has been closed).
    document.body.style.removeProperty('overflow');
  }

  @state()
  _hasAccessibilityDescriptionSlot: boolean;

  connectedCallback() {
    super.connectedCallback();
    this._handleSlotChange();
  }

  private _handleSlotChange(): void {
    this._hasAccessibilityDescriptionSlot =
      this.querySelector('[slot="accessibility-description"]') !== null;
  }

  private _modalTemplate(): TemplateResult {
    let template = html``;

    if (this.isOpen) {
      template = html`
        <section modal tabindex="-1" class="modal">
          <div
            tabindex="-1"
            @click="${this._handleOverlayClick}"
            @keydown="${this._handleOverlayKeydown}"
          >
            <div
              class="modal-container"
              role="dialog"
              aria-modal="true"
              aria-describedby="${ifDefined(
                this._hasAccessibilityDescriptionSlot
                  ? 'accessibility-description'
                  : undefined
              )}"
            >
              <div class="modal-content">
                <button
                  focusable="true"
                  class="close btn primary large rounded"
                  aria-label="Close modal"
                  @click="${this.close}"
                  @keydown="${this._handleCloseKeydown}"
                >
                  <outline-icon-baseline name="eu-x" size="1rem">
                  </outline-icon-baseline>
                </button>

                ${this.slots.conditionalSlot('video', false)}
              </div>
            </div>
          </div>
          <slot
            id="accessibility-description"
            name="accessibility-description"
            @slotchange="${this._handleSlotChange}"
          ></slot>
        </section>
      `;
    }

    return template;
  }

  async open(): Promise<void> {
    if (!this.isOpen) {
      this.isOpen = true;

      await this.updateComplete;

      const closeButton: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector('.close');

      if (closeButton) {
        closeButton.focus();
      }

      // disable page scrolling functionality while modal is open
      document.body.style.overflow = 'hidden';

      this._trapFocus();

      this.dispatchEvent(new CustomEvent('opened'));
    }
  }

  async close(): Promise<void> {
    if (this.isOpen) {
      this.isOpen = false;

      await this.updateComplete;

      // restore page scrolling
      document.body.style.removeProperty('overflow');

      this.dispatchEvent(new CustomEvent('closed'));

      this.triggerElement.focus();
    }
  }

  @query('.trigger')
  private triggerElement!: HTMLDivElement;

  private _handleTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // This prevents a focused element from also triggering.
      // For example, the modal opens and the "accept" button is focused and then triggered and the modal closes.
      event.preventDefault();

      this.open();
    }
  }

  private _handleOverlayClick(event: MouseEvent): void {
    // Only trigger if we click directly on the event that wants to receive the click.
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  private _handleOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  // For some reason on the `Docs` tab of Storybook, the `click` event for the close button doesn't work with the `Enter` key without also watching the `keyup` event. This isn't the case on the `Canvas` tab.
  private _handleCloseKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.close();
    }
  }

  @property({ type: String })
  elementToFocusSelector?: string | undefined;

  private _trapFocus(): void {
    const modalElement = this.shadowRoot?.querySelector('.modal');

    // Define a selector that matches elements that can be focused by default
    const defaultFocusableSelector =
      'a[href], button, input, select, textarea, [tabindex]';

    // Query all the elements that match the selector in the document
    const defaultFocusableElements = modalElement?.querySelectorAll(
      defaultFocusableSelector
    );

    const firstFocusableElement =
      defaultFocusableElements && (defaultFocusableElements[0] as HTMLElement);
    const lastFocusableSlottedElement =
      defaultFocusableElements &&
      (defaultFocusableElements[
        defaultFocusableElements.length - 1
      ] as HTMLElement);

    if (firstFocusableElement) {
      const lastFocusableElement =
        lastFocusableSlottedElement ?? firstFocusableElement;

      if (lastFocusableElement) {
        lastFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey === false) {
              event.preventDefault();
              firstFocusableElement.focus();
            }
          }
        );
      }

      if (firstFocusableElement) {
        firstFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey) {
              event.preventDefault();
              if (lastFocusableElement) {
                lastFocusableElement.focus();
              }
            }
          }
        );
      }
    }
  }

  playIconTemplate(): TemplateResult | null {
    return html`
      <div class="play-overlay ${this.isOpen ? 'is-open' : null}">
        <div class="btn primary large rounded">
          <outline-icon-baseline name="eu-triangle" size="2rem">
          </outline-icon-baseline>
        </div>
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-video-modal': OutlineVideoModal;
  }
}
